import { Field, ID, InputType } from 'type-graphql'
import { ActionArgs } from '../../action/ActionArgs'

@InputType()
export class TransferCreditsArgs extends ActionArgs {
  /** Source `CreditAccount.ownerId` — its ENTIRE current balance is what moves. */
  @Field(() => ID)
  fromOwnerId: string

  /** Destination `CreditAccount.ownerId`, created via `createIfMissing` when it has no account yet. */
  @Field(() => ID)
  toOwnerId: string

  /** `remarks` stamped on the source account's own ledger row. */
  @Field(() => String, { nullable: true })
  remarksFrom?: string

  /** `remarks` stamped on the destination account's own ledger row. */
  @Field(() => String, { nullable: true })
  remarksTo?: string

  /** `createdBy` stamped on both ledger rows. */
  @Field(() => ID, { nullable: true })
  actingUserId?: string
}

/**
 * Moves an owner's ENTIRE current balance to another owner as a paired `Adjustment` ledger
 * entry on each side — the sole caller of the balance-gated compare-and-set `atomicUpdateOne`
 * this needs, so no other module hand-rolls it. The source is debited FIRST, via an atomic
 * filtered `$inc` gated on the exact balance just read (an `atomicUpdateOne` filter miss is a
 * normal, non-crash outcome — not just a crash-recovery path — so this MUST run before the
 * destination is ever touched): a concurrent debit of the source between the read and this
 * write cannot double-spend it, and a lost race throws with NEITHER account mutated, a clean
 * retryable no-op. Only the winner of that CAS then credits the destination (creating its
 * `CreditAccount` via `createIfMissing` if missing). This closes the credit-inflation exposure
 * the reverse (destination-first) order had — two concurrent callers transferring the same
 * source balance (e.g. two concurrent `JoinSchool` calls for the same teacher, or a retry after
 * a concurrent scan debit changed the source mid-transfer) could each have the CAS lose only
 * AFTER the destination was already credited, so the pool gained twice what the teacher had.
 * The residual window this ordering still leaves — source debited, destination not yet
 * credited — is crash-only (no live caller reaches it without a crash) and is accepted rather
 * than closed with a durable transfer-intent claim, which would be a materially larger change
 * than reordering these two writes. Cross-module consumers (e.g. QuickScore's `JoinSchool`)
 * inject this by `IocKey.ai.service.transferCredits`, typed via this interface — the concrete
 * class stays internal to `ai-node`.
 */
export interface ITransferCredits {
  execute(args: TransferCreditsArgs): Promise<void>
}
